/**
 * The shuffle.
 *
 * Two properties, and both of them have to hold at once.
 *
 * UNPREDICTABLE. The order must not be inferrable from anything the player can see or
 * compute. That rules out a small-state generator however good its statistics look:
 * a 52-card deck has 52! orderings — 8.07 x 10^67, or 2^225.6 — so a seed shorter than
 * 226 bits makes most decks impossible before a card is dealt, and the ones that remain
 * can be enumerated. A 32-bit seed leaves 4.3 billion of them, which is what got an
 * online poker room's shuffle broken in 1999. The seed here is 256 bits and the stream
 * is ChaCha20.
 *
 * REPRODUCIBLE FROM ITS SEED. A hand history is only a replay if the hand can be dealt
 * again, and a player can only check that a deck was not rigged if they can recompute
 * it. Those are the same requirement. Randomness lives in the seed, and nowhere else.
 */
import { CryptoRng, fromHex, type CryptoSeed } from "./rng";
import type { Card } from "./types";

export const DECK_SIZE = 52;

/** Accepts a seed as bytes or as the hex string a hand history records. */
export function seedBytes(seed: CryptoSeed): Uint8Array {
  return typeof seed === "string" ? fromHex(seed) : seed;
}

/**
 * A shuffled deck, from a 256-bit seed.
 *
 * Fisher-Yates, downward, with an UNBIASED index at each step. `next % (i+1)` is the
 * usual way this is written and it is subtly wrong: 2^32 is not divisible by most
 * values of i+1, so low indices come up slightly more often and certain cards drift
 * toward certain positions. The effect is around one part in eighty million — far too
 * small to notice at the table, and the first thing a review would find. `below()`
 * rejects the biased tail instead.
 */
export function shuffledDeck(seed: CryptoSeed): Card[] {
  const deck: Card[] = Array.from({ length: DECK_SIZE }, (_, i) => i);
  shuffleInPlace(deck, new CryptoRng(seedBytes(seed), "deck"));
  return deck;
}

/**
 * The shuffle itself, over any array.
 *
 * Separated from `shuffledDeck` only so that it can be tested at a size where every
 * permutation can be counted. 52! orderings cannot be enumerated, so uniformity over
 * a full deck can only ever be checked one marginal at a time; over five elements
 * there are 120 permutations and a chi-square test covers all of them at once. It is
 * the same code either way — a battery that tested a reimplementation would prove
 * nothing about the deck players are dealt.
 */
export function shuffleInPlace<T>(items: T[], rng: CryptoRng): T[] {
  for (let i = items.length - 1; i > 0; i--) {
    const j = rng.below(i + 1);
    const t = items[i]!; items[i] = items[j]!; items[j] = t;
  }
  return items;
}

/**
 * Is this a legal deck?
 *
 * Every card exactly once, nothing outside the deck. Cheap enough to assert on every
 * deal, and it is the property that a shuffle bug destroys first — a swap written the
 * wrong way round duplicates a card rather than producing an unusual order, and a
 * duplicated card is a broken game rather than an unlucky one.
 */
export function isLegalDeck(deck: readonly Card[]): boolean {
  if (deck.length !== DECK_SIZE) return false;
  const seen = new Uint8Array(DECK_SIZE);
  for (const c of deck) {
    if (!Number.isInteger(c) || c < 0 || c >= DECK_SIZE || seen[c]) return false;
    seen[c] = 1;
  }
  return true;
}
